import { getA11yClickProps } from 'app/shared/utils/a11y';
import { HTMLProps, MouseEvent, ReactElement, ReactNode } from 'react';
import { AnalyticsEvents, trackEvent } from 'app/shared/u21-ui/analytics';
import styled, { css } from 'styled-components';
import { CardHeader } from '@mui/material';
import { IconChevronDown, IconChevronUp } from '@u21/tabler-icons';
import {
  U21HelpModal,
  U21HelpModalProps,
} from 'app/shared/u21-ui/components/display/U21HelpModal';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21TypographyIcon } from 'app/shared/u21-ui/components/display/typography/U21TypographyIcon';
import { getDOMProps } from 'app/shared/utils/react';

export interface U21SectionHeaderProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'action'
> {
  action?: ReactNode;
  children?: ReactNode;
  collapsed?: boolean;
  collapsible?: boolean;
  disableTitleCase?: boolean;
  /**
   * @deprecated should use info alert instead
   */
  helpModalProps?: U21HelpModalProps;
  icon?: ReactElement | null;
  onToggleCollapse?: (collapsed: boolean, e: MouseEvent<HTMLElement>) => void;
  subheader?: ReactNode;
  mounted?: boolean;
}

export const U21SectionHeader = (props: U21SectionHeaderProps) => {
  const {
    action,
    children,
    collapsed,
    collapsible,
    disableTitleCase,
    helpModalProps,
    onToggleCollapse,
    subheader,
    icon,
    ...rest
  } = props;

  const collapsibleIcon = collapsible && (
    <U21TypographyIcon variant="h4">
      {collapsed ? <IconChevronDown /> : <IconChevronUp />}
    </U21TypographyIcon>
  );

  return (
    <StyledCardHeader
      $disableTitleCase={disableTitleCase}
      $collapsible={collapsible}
      action={
        action && collapsibleIcon ? (
          <U21Spacer horizontal spacing={2}>
            {action}
            {collapsibleIcon}
          </U21Spacer>
        ) : (
          action || collapsibleIcon
        )
      }
      avatar={
        icon && <U21TypographyIcon variant="h4">{icon}</U21TypographyIcon>
      }
      title={
        helpModalProps ? (
          <U21Spacer horizontal>
            {children}
            <U21HelpModal {...helpModalProps} />
          </U21Spacer>
        ) : (
          children
        )
      }
      subheader={subheader}
      {...getDOMProps(rest)}
      {...getA11yClickProps(
        onToggleCollapse
          ? (event: MouseEvent<HTMLElement>) => {
              trackEvent(
                collapsed
                  ? AnalyticsEvents.U21SECTION_ON_EXPAND
                  : AnalyticsEvents.U21SECTION_ON_COLLAPSE,
                props,
                {},
                event,
              );
              onToggleCollapse(!collapsed, event);
            }
          : undefined,
        !collapsible,
      )}
    />
  );
};

const StyledCardHeader = styled(CardHeader)<{
  $collapsible?: boolean;
  $disableTitleCase?: boolean;
}>`
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 24px;
  border-radius: ${(props) =>
      `${props.theme.shape.borderRadiusMd}px ${props.theme.shape.borderRadiusMd}px`}
    0 0;

  // set min-width for content when subheader is present
  .MuiCardHeader-content:has(.MuiCardHeader-subheader) {
    flex-basis: 400px;
    min-width: 0;
  }

  .MuiCardHeader-action {
    ${(props) =>
      props.action
        ? css`
            margin-left: auto;
          `
        : css``}
  }

  ${(props) =>
    props.$collapsible
      ? css`
          cursor: pointer;
        `
      : css``}

  ${(props) =>
    props.$disableTitleCase
      ? css``
      : css`
          .MuiCardHeader-title {
            text-transform: capitalize;
          }
        `}
`;
